import React, { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import Button from '../elements/Button'
import { Portal } from '@material-ui/core'
import PropTypes from 'prop-types'
import AjaxLoader from '../components/AjaxLoader'
import Error from '../components/Error'

const CalendarContainer = ({ children }) => {
    const el = document.getElementById('portal')
    return <Portal container={el}>{children}</Portal>
}

const SpeedBoostScheduleForm = props => {
    const {
        scheduledStartTime,
        scheduleStartDate,
        scheduleEndDate,
        boostBankDays,
        boostMonthDays,
        boostDayCost,
        scheduleEnabled,
        scheduledDays
    } = props.scheduleData

    const [speedBoostData, setSpeedBoostData] = useState({
        startTime: scheduledStartTime,
        boostDaysNeeded: scheduledDays,
        startDate: scheduleStartDate,
        endDate: scheduleEndDate
    })

    const [cost, setCost] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        setSpeedBoostData({
            startTime: scheduledStartTime,
            boostDaysNeeded: scheduledDays || 1,
            startDate: toDate(scheduleStartDate) > new Date() ? toDate(scheduleStartDate) : addDays(toDate(scheduleStartDate), 1),
            endDate: toDate(scheduleEndDate) > new Date() ? toDate(scheduleEndDate) : addDays(toDate(scheduleEndDate), 2)
        })
    }, [props.scheduleData])

    useEffect(() => {
        setIsLoading(false)
    }, [props.isSaved, props.isDeleted, props.scheduleError])

    useEffect(() => {
        calculateTotalCharge()
    }, [speedBoostData])

    const addDays = (theDate, days) => {
        const new_date = moment(theDate).add(days, 'days')
        return new Date(new_date)
    }

    // format the date
    const toDate = (dateString) => {
        const date = moment(dateString).toDate()
        const currentDate = new Date()
        if (dateString === null) {
            return currentDate
        }
        return date
    }

    // handle input change dynamically.
    const handleChange = e => {
        const { name, value } = e.target
        setErrorMessage('')
        setSpeedBoostData(prevState => ({
            ...prevState,
            [name]: value
        }))
    }

    const handleStartDatePicker = date => {
        if (date > speedBoostData.endDate || (date.toDateString() === speedBoostData.endDate.toDateString())) {
            setSpeedBoostData(speedBoostData => ({
                ...speedBoostData,
                startDate: date,
                endDate: addDays(date, 1),
                boostDaysNeeded: 1
            }))
        } else {
            let daysDiff
            if (speedBoostData.endDate) {
                daysDiff = moment(speedBoostData.endDate.toDateString()).diff(moment(date.toDateString()), 'days')
            }

            setSpeedBoostData(speedBoostData => ({
                ...speedBoostData,
                startDate: date,
                boostDaysNeeded: daysDiff
            }))
        }
    }

    const handleEndDatePicker = date => {
        let daysDiff
        if (speedBoostData.startDate) {
            daysDiff = moment(date.toDateString()).diff(moment(speedBoostData.startDate.toDateString()), 'days')
        }

        setSpeedBoostData(speedBoostData => ({
            ...speedBoostData,
            endDate: date,
            boostDaysNeeded: daysDiff
        }))
    }

    const onSubmit = e => {
        e.preventDefault()
        if (speedBoostData.startTime !== '' && speedBoostData.startTime !== null) {
            setIsLoading(true)
            props.onSubmitData(speedBoostData)
            setErrorMessage('')
        } else {
            setErrorMessage('Start time field is required!')
        }
    }

    const onDelete = (e) => {
        e.preventDefault()
        setIsLoading(true)
        setSpeedBoostData({
            startTime: '',
            boostDaysNeeded: null,
            startDate: '',
            endDate: ''
        })
        props.onDeleteSchedule()
    }

    const calculateTotalCharge = () => {
        let totalCharge
        if (boostBankDays + boostMonthDays - speedBoostData.boostDaysNeeded < 0) {
            totalCharge = Math.abs((boostBankDays + boostMonthDays - speedBoostData.boostDaysNeeded) * parseInt(boostDayCost))
            setCost(totalCharge)
        } else {
            setCost(0)
        }
    }

    return (
        <React.Fragment>
            <div className='schedule-container text-center'>
                <h2 className='color-indigo' style={{ textTransform: 'uppercase' }}>Set multi-day boost & schedule</h2>
                <p className='text-left lh'>
                    You can set <span className='bold color-indigo'>My Speed Boost™</span> to
                    automatically upgrade your service speed from a specific
                    time and date, for a number of days. Check out your
                    available days in your{' '}
                    <span className='bold color-indigo'>Available Speed Upgrade Days </span>
                    table.
                </p>
            </div>

            {(!isLoading)
                ? (
                    <React.Fragment>
                        <form className='schedule-form'>
                            <div className='row'>
                                <div className='col-12 col-lg-6 form-group mt-lg-0 mt-3'>
                                    <label className='bold color-indigo'>Upgrade Days (Start date)</label>
                                    <DatePicker
                                        className='mt-2'
                                        selected={speedBoostData.startDate}
                                        minDate={addDays(new Date(), 1)}
                                        dateFormat='dd/MM/yyyy'
                                        onChange={handleStartDatePicker}
                                        popperContainer={CalendarContainer}
                                        disabled={scheduleEnabled}
                                    />
                                </div>
                                <div className='col-12 col-lg-6 form-group mt-lg-0 mt-3'>
                                    <label className='bold color-indigo'>Upgrade Days (End date)</label>
                                    <DatePicker
                                        className='mt-2'
                                        selected={speedBoostData.endDate}
                                        minDate={addDays(speedBoostData.startDate, 1)}
                                        dateFormat='dd/MM/yyyy'
                                        onChange={handleEndDatePicker}
                                        popperContainer={CalendarContainer}
                                        disabled={scheduleEnabled}
                                    />
                                </div>
                                <div className='col-12 col-lg-6 form-group mt-3'>
                                    <label htmlFor='startTime' className='bold color-indigo'>Start Time</label>
                                    <input
                                        className={`mt-2 ${errorMessage !== '' ? 'error' : ''}`}
                                        id='startTime'
                                        type='time'
                                        name='startTime'
                                        onChange={handleChange}
                                        value={speedBoostData.startTime || ''}
                                        disabled={scheduleEnabled}
                                    />
                                </div>
                                <div className='col-12 col-lg-6 form-group mt-3'>
                                    <label htmlFor='boostDaysNeeded' className='bold color-indigo'>
                                        Total Upgrade Days
                                    </label>
                                    <input
                                        className='mt-2 bg-lightgrey'
                                        id='boostDaysNeeded'
                                        type='number'
                                        name='boostDaysNeeded'
                                        value={speedBoostData.boostDaysNeeded || 1}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='mt-4 col-md-6 col-12'>
                                    {scheduleEnabled && <button type='submit' onClick={onDelete} className='clear-date-btn btn btn-custom'>Clear schedule</button>}
                                </div>
                                <div className='mt-4 col-md-6 col-12'>
                                    {!scheduleEnabled && <Button action={onSubmit} className='save-date-btn'>Save schedule</Button>}
                                </div>
                            </div>
                        </form>
                        <div className='text-center mt-4 price-row font-smaller'>
                            Total charge: <span className='bold'>${cost}.00</span>
                        </div>
                        {errorMessage !== '' && <div className='row-error mt-3'>{errorMessage}</div>}
                        {props.scheduleError && <div className='mt-3'><Error errors={props.scheduleError} /></div>}
                    </React.Fragment>
                )
                : <div className='d-flex w-100 justify-content-center align-items-center'>
                    <AjaxLoader />
                </div>}
        </React.Fragment>
    )
}

SpeedBoostScheduleForm.propTypes = {
    scheduleData: PropTypes.object.isRequired,
    onSubmitData: PropTypes.func.isRequired,
    onDeleteSchedule: PropTypes.func.isRequired,
    isSaved: PropTypes.bool.isRequired,
    isDeleted: PropTypes.bool.isRequired,
    scheduleError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ])
}

export default SpeedBoostScheduleForm



// WEBPACK FOOTER //
// ./resources/js/modules/SpeedBoostScheduleForm.js